import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import React, { FC, useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowDropdown } from '../../../assets/images/arrows/ArrowDefault.svg';

interface CustomSelectProps {
  disabled?: boolean;
  value: number | string;
  label?: string;
  items: OptionElement[];
  clickTabValue?: (id: number | string) => void;
  setValue: (event: SelectChangeEvent<string | number>, newValue: number | string) => void;
  isAutoPayAvailable?: boolean;
  hasSearch?: boolean;
}

interface OptionElement {
  label: string;
  id: number | string;
}

const defaultProps = {
  label: undefined,
  disabled: false,
  clickTabValue: undefined,
  isAutoPayAvailable: false,
  hasSearch: false,
};

export const CustomSelect: FC<CustomSelectProps> = ({
  disabled,
  value,
  setValue,
  label,
  items,
  clickTabValue,
  isAutoPayAvailable,
  hasSearch,
}) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isOpened) {
      setSearchTerm('');
    } else if (hasSearch) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpened, hasSearch]);

  const filteredItems = useMemo(() => {
    if (!hasSearch || searchTerm.trim() === '') return items;
    return items.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [items, searchTerm, hasSearch]);

  return (
    <FormControl fullWidth className="custom-select">
      <InputLabel id="demo-simple-select-label">{label}</InputLabel>
      <Select
        disabled={disabled}
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={value}
        onChange={(e) => setValue(e, e.target.value)}
        displayEmpty={label === undefined}
        label={label}
        MenuProps={{ classes: { paper: 'custom-select-paper' }, variant: 'menu' }}
        onOpen={() => setIsOpened(true)}
        onClose={() => setIsOpened(false)}
        IconComponent={() => <ArrowDropdown className={isOpened ? 'icon-opened' : ''} />}
      >
        {hasSearch && (
          <MenuItem
            disableRipple
            style={{ cursor: 'default', backgroundColor: 'transparent' }}
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              type="text"
              value={searchTerm}
              fullWidth
              inputRef={inputRef}
              onKeyDown={(e) => e.stopPropagation()}
              size="small"
              className="input input__text-field__primary"
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              InputProps={{ autoComplete: 'off' }}
            />
          </MenuItem>
        )}
        {filteredItems.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            onClick={clickTabValue ? () => clickTabValue(option.id) : undefined}
            disabled={isAutoPayAvailable && option.label === t('accountSettings.regularPayment')}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

CustomSelect.defaultProps = defaultProps;
